import { useMemo, useState } from "react";
import { Spinner } from "./ui";
import { fmtDate } from "../lib/format.js";
import { btnG, btnP, glass, heading, input, monoFont } from "../styles.js";

const CalendarPane = ({
    visits,
    notes,
    clients,
    properties,
    session,
    onOpenClient,
    onOpenProperty,
    onAddNote,
    onDeleteNote,
    onChanged,
  }) => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
    const [selected, setSelected] = useState(todayStr);
    const cById = Object.fromEntries(clients.map((c) => [c.id, c]));
    const pById = Object.fromEntries(properties.map((p) => [p.id, p]));
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const monthName = cursor.toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });
    const firstDay = new Date(year, month, 1);
    const startOffset = firstDay.getDay(); // 0 = Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // 6-row grid (always — keeps layout stable)
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push(ds);
    }
    while (cells.length < 42) cells.push(null);

    // Index visits + notes by YYYY-MM-DD (timezone-safe — use UTC slice)
    const visitsByDate = useMemo(() => {
      const m = {};
      visits.forEach((v) => {
        const t = v.visited_at;
        if (!t) return;
        const d = new Date(t);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        (m[key] = m[key] || []).push(v);
      });
      return m;
    }, [visits]);
    const notesByDate = useMemo(() => {
      const m = {};
      notes.forEach((n) => {
        if (n.note_date) (m[n.note_date] = m[n.note_date] || []).push(n);
      });
      return m;
    }, [notes]);
    const goMonth = (delta) => setCursor(new Date(year, month + delta, 1));
    const goToday = () => {
      setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
      setSelected(todayStr);
    };
    const fmtTime = (t) => {
      try {
        return new Date(t).toLocaleTimeString("en-IN", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      } catch {
        return "";
      }
    };
    const fmtSelectedHeader = (ds) => {
      try {
        const d = new Date(ds + "T00:00:00");
        return d.toLocaleDateString("en-IN", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      } catch {
        return ds;
      }
    };

    // Add-note inline form
    const [showAdd, setShowAdd] = useState(false);
    const [nTitle, setNTitle] = useState("");
    const [nDesc, setNDesc] = useState("");
    const [saving, setSaving] = useState(false);
    const saveNote = async () => {
      if (!nTitle.trim()) return;
      setSaving(true);
      try {
        await onAddNote({
          note_date: selected,
          title: nTitle.trim(),
          description: nDesc.trim() || null,
        });
        setNTitle("");
        setNDesc("");
        setShowAdd(false);
      } catch (e) {
        alert("Couldn't save note: " + e.message);
      }
      setSaving(false);
    };
    const dayVisits = visitsByDate[selected] || [];
    const dayNotes = notesByDate[selected] || [];
    const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return (
      <div className="space-y-4">
        <div className={`${glass} rounded-2xl p-4`}>
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => goMonth(-1)}
              className="p-1.5 rounded-lg hover:bg-white/[0.06] text-slate-300"
              aria-label="Previous month"
            >
              ‹
            </button>
            <div className="flex items-center gap-2">
              <h2 className="text-lg text-slate-100" style={heading}>
                {monthName}
              </h2>
              <button
                onClick={goToday}
                className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.08] text-slate-300 hover:text-slate-100"
              >
                Today
              </button>
            </div>
            <button
              onClick={() => goMonth(1)}
              className="p-1.5 rounded-lg hover:bg-white/[0.06] text-slate-300"
              aria-label="Next month"
            >
              ›
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAYS.map((w) => (
              <div
                key={w}
                className="text-center text-[10px] uppercase tracking-wider text-slate-500 py-1"
              >
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((ds, idx) => {
              if (!ds) return <div key={"e" + idx} className="aspect-square" />;
              const isToday = ds === todayStr;
              const isSelected = ds === selected;
              const v = visitsByDate[ds] || [];
              const n = notesByDate[ds] || [];
              const has = v.length + n.length;
              return (
                <button
                  key={ds}
                  onClick={() => setSelected(ds)}
                  className={`aspect-square rounded-lg p-1.5 text-left transition relative flex flex-col ${isSelected ? "bg-amber-300 text-slate-900" : isToday ? "bg-white/[0.08] text-slate-100 ring-1 ring-amber-300/40" : "bg-white/[0.02] hover:bg-white/[0.06] text-slate-300"}`}
                >
                  <span className="text-xs font-medium" style={monoFont}>
                    {Number(ds.slice(8))}
                  </span>
                  {has > 0 && (
                    <div className="mt-auto flex items-center gap-0.5 flex-wrap">
                      {v.slice(0, 3).map((_, i) => (
                        <span
                          key={"v" + i}
                          className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-slate-900/70" : "bg-emerald-400"}`}
                        />
                      ))}
                      {n.slice(0, 3).map((_, i) => (
                        <span
                          key={"n" + i}
                          className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-slate-900/70" : "bg-amber-300"}`}
                        />
                      ))}
                      {has > 6 && (
                        <span
                          className={`text-[8px] ml-0.5 ${isSelected ? "text-slate-900/70" : "text-slate-500"}`}
                        >
                          +{has - 6}
                        </span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/[0.06]">
            <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              Visits
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
              <span className="w-2 h-2 rounded-full bg-amber-300" />
              Notes
            </div>
          </div>
        </div>
        <div className={`${glass} rounded-2xl p-4 space-y-3`}>
          <div className="flex items-center justify-between gap-2">
            <div>
              <h3 className="text-base text-slate-100" style={heading}>
                {fmtSelectedHeader(selected)}
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {dayVisits.length} visit{dayVisits.length === 1 ? "" : "s"} · {dayNotes.length} note
                {dayNotes.length === 1 ? "" : "s"}
              </p>
            </div>
            <button
              onClick={() => {
                setShowAdd((s) => !s);
                setNTitle("");
                setNDesc("");
              }}
              className={btnG + " text-xs"}
            >
              {showAdd ? "Cancel" : "+ Add note"}
            </button>
          </div>
          {showAdd && (
            <div className="space-y-2 p-3 rounded-lg bg-amber-300/5 border border-amber-300/15">
              <input
                autoFocus={true}
                value={nTitle}
                onChange={(e) => setNTitle(e.target.value)}
                placeholder="Note title (e.g. 'Meet bank rep')"
                className={input}
              />
              <textarea
                value={nDesc}
                onChange={(e) => setNDesc(e.target.value)}
                placeholder="Details (optional)…"
                rows={2}
                className={input + " resize-y"}
              />
              <div className="flex justify-end">
                <button
                  onClick={saveNote}
                  disabled={saving || !nTitle.trim()}
                  className={btnP + " text-xs disabled:opacity-50"}
                >
                  {saving ? <Spinner size={12} /> : null} Save note for this day
                </button>
              </div>
            </div>
          )}
          {dayVisits.length === 0 && dayNotes.length === 0 && !showAdd && (
            <div className="text-center text-sm text-slate-500 py-6">
              Nothing scheduled for this day.
            </div>
          )}
          {dayNotes.map((n) => (
            <div
              key={n.id}
              className="p-3 rounded-lg bg-amber-300/8 border border-amber-300/15 flex items-start gap-3"
            >
              <div className="w-1 self-stretch rounded-full bg-amber-300/60" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-amber-100" style={heading}>
                  {n.title}
                </div>
                {n.description && (
                  <div className="text-xs text-slate-300 mt-1 whitespace-pre-wrap">
                    {n.description}
                  </div>
                )}
                <div className="text-[10px] text-slate-500 mt-1.5">
                  Note · added {fmtDate(n.created_at)}
                </div>
              </div>
              {n.created_by === session.user.id && (
                <button
                  onClick={() => {
                    if (confirm("Delete this note?")) onDeleteNote(n.id);
                  }}
                  className="text-slate-500 hover:text-rose-300 text-xs p-1"
                  aria-label="Delete note"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          {dayVisits
            .sort((a, b) => new Date(a.visited_at) - new Date(b.visited_at))
            .map((v) => {
              const c = v.client_id ? cById[v.client_id] : null;
              const p = v.property_id ? pById[v.property_id] : null;
              const who = c ? c.name : v.walkin_name || "Walk-in";
              return (
                <div
                  key={v.id}
                  className="p-3 rounded-lg bg-emerald-400/8 border border-emerald-400/15 flex items-start gap-3"
                >
                  <div className="w-1 self-stretch rounded-full bg-emerald-400/60" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-100" style={heading}>
                      {who} {p && <span className="text-slate-400 font-normal">· {p.title}</span>}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {fmtTime(v.visited_at)}
                      {v.interested === true && (
                        <span className="ml-2 text-emerald-200">· interested</span>
                      )}
                      {v.interested === false && (
                        <span className="ml-2 text-stone-400">· not interested</span>
                      )}
                    </div>
                    {v.note && (
                      <div className="text-xs text-slate-300 mt-1.5 whitespace-pre-wrap">
                        {v.note}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      {c && (
                        <button
                          onClick={() => onOpenClient(c.id)}
                          className="text-[11px] text-amber-200 hover:underline"
                        >
                          View client
                        </button>
                      )}
                      {p && (
                        <button
                          onClick={() => onOpenProperty(p.id)}
                          className="text-[11px] text-teal-300 hover:underline"
                        >
                          View property
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    );
  };

export { CalendarPane };
